import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { FileText, Trash2, Save, Loader2 } from 'lucide-react';

// Modal for creating / editing / deleting custom note templates.
// All state lives in the parent (custom-template hook). This component only
// renders the form and dispatches the four primitive actions.
export function CustomTemplateModal({
  open,
  onOpenChange,
  editingId,
  name,
  setName,
  labelsText,
  setLabelsText,
  guidelines,
  setGuidelines,
  saving,
  onSave,
  onDelete,
}) {
  const visibleLabels = labelsText.split('\n').map((l) => l.trim()).filter(Boolean);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg rounded-3xl max-h-[85vh] overflow-y-auto" data-testid="custom-template-modal">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-[#1E3329] flex items-center gap-2">
            <FileText className="w-5 h-5" />
            {editingId ? 'Edit custom template' : 'Create custom template'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wide text-[#2C4A3B]">Template name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. My Follow-up Psych Note"
              data-testid="custom-template-name-input"
              className="mt-1 w-full rounded-lg text-sm p-2.5 border border-[#2C4A3B] text-[#1E3329] focus:outline-none focus:ring-2 focus:ring-[#2C4A3B]"
            />
          </div>

          <div>
            <label className="text-[11px] font-bold uppercase tracking-wide text-[#2C4A3B]">Section labels (one per line, in order)</label>
            <textarea
              value={labelsText}
              onChange={(e) => setLabelsText(e.target.value)}
              rows={8}
              placeholder={'CC\nHPI\nMSE\nAssessment\nPlan'}
              data-testid="custom-template-labels-input"
              className="mt-1 w-full rounded-lg text-sm p-2.5 border border-[#2C4A3B] text-[#1E3329] font-mono focus:outline-none focus:ring-2 focus:ring-[#2C4A3B] resize-y"
            />
            <p className="text-[11px] text-[#6B8E7B] mt-1">
              These labels become the section headers of your note. Order matters — the LLM writes each section in the order you list them.
            </p>
          </div>

          {visibleLabels.length > 0 && (
            <div>
              <label className="text-[11px] font-bold uppercase tracking-wide text-[#2C4A3B]">Guidelines (optional — helps the LLM write the right content for each section)</label>
              <div className="mt-1 space-y-2">
                {visibleLabels.map((lbl, i) => (
                  <div key={`${lbl}-${i}`} className="flex items-start gap-2">
                    <span className="text-[11px] font-semibold text-[#2C4A3B] mt-2 shrink-0 w-28 truncate" title={lbl}>{lbl}</span>
                    <input
                      type="text"
                      value={guidelines[lbl] || ''}
                      onChange={(e) => setGuidelines((prev) => ({ ...prev, [lbl]: e.target.value }))}
                      placeholder={`How to write the "${lbl}" section…`}
                      data-testid={`custom-template-guideline-${lbl.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                      className="flex-1 rounded-lg text-xs p-2 border border-[#E3E8E4] text-[#1E3329] focus:outline-none focus:ring-2 focus:ring-[#2C4A3B]"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between gap-2 pt-2">
            {editingId ? (
              <Button
                onClick={() => onDelete()}
                disabled={saving}
                variant="outline"
                data-testid="custom-template-delete-btn"
                className="rounded-xl border-red-300 text-red-700 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4 mr-1.5" /> Delete
              </Button>
            ) : <div />}
            <div className="flex gap-2">
              <Button
                onClick={() => onOpenChange(false)}
                disabled={saving}
                variant="outline"
                className="rounded-xl"
              >
                Cancel
              </Button>
              <Button
                onClick={onSave}
                disabled={saving}
                data-testid="custom-template-save-btn"
                className="rounded-xl bg-gradient-to-r from-green-600 to-emerald-700 hover:from-green-700 hover:to-emerald-800 text-white"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                {editingId ? 'Update template' : 'Create template'}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
